import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { cleanContactSyncGroups } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { ContactSyncModel } from '../../../../types';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteLabelsDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedContactSync: ContactSyncModel;
  changeLogRoles: string[];
}

export default function DeleteLabelsDialog({
  open,
  handleClose,
  selectedContactSync,
  changeLogRoles,
}: DeleteLabelsDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      cleanContactSyncGroups(selectedContactSync, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'The task to delete the labels of the contact sync has begun.',
      });

      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue deleting the labels of the contact sync. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.directory.contact_sync.delete_contact_sync_labels, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.contactSync,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        DirectoryRoles.RemoveContactSyncLabel.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Delete Labels"
      dialogContent={
        <Box>
          <Typography>
            Do you wish to delete the labels and contacts associated with{' '}
            <strong>{selectedContactSync.name}</strong> for all destination
            users?
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Delete',
        color: 'error',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
